import { useState } from 'react';

import { DataTable } from '../components/DataTable.tsx';
import { ItemIcon } from '../components/ItemIcon.tsx';
import { Badge, Card, Empty, Notice, Spinner } from '../components/Ui.tsx';
import {
  ApiError,
  UpgradeRequiredError,
  api,
  type CreateConnectionResult,
  type TestPayload,
} from '../lib/api.ts';
import { useAsync } from '../lib/auth.tsx';
import { number, relativeTime, unitMoney } from '../lib/format.ts';

const STATUS_TONE = {
  verified: 'good',
  pending: 'warning',
  failed: 'critical',
  disabled: 'muted',
} as const;

export function OnboardingPage() {
  const connections = useAsync(() => api.onboarding.connections(), []);
  const [created, setCreated] = useState<CreateConnectionResult | null>(null);
  const [testPayload, setTestPayload] = useState<TestPayload | null>(null);
  const [form, setForm] = useState({
    accountType: 'firm' as 'firm' | 'personal',
    dcAccountCode: '',
    privacyAcknowledged: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.onboarding.create({
        accountType: form.accountType,
        dcAccountCode: form.dcAccountCode.trim(),
        ...(form.accountType === 'personal'
          ? { privacyAcknowledged: form.privacyAcknowledged }
          : {}),
      });
      setCreated(result);
      setForm({ accountType: 'firm', dcAccountCode: '', privacyAcknowledged: false });
      connections.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the connection');
    } finally {
      setBusy(false);
    }
  };

  if (connections.loading) return <Spinner />;
  const list = connections.data?.connections ?? [];
  const allowance = connections.data?.allowance;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Connected accounts</h1>
          <p>
            Ingestion runs on a webhook you configure on your own in-game account. You keep your API
            key; we only ever receive the event payloads, and every one is signature-verified before
            it is stored.
          </p>
        </div>
      </header>

      <ClaimShops onClaimed={connections.reload} />

      {allowance && (
        <div style={{ marginBottom: 16 }}>
          <Notice>
            <strong>
              {allowance.personalUsed}/{allowance.personalLimit} personal and{' '}
              {allowance.firmsUsed}/{allowance.firmLimit} firm account
              {allowance.firmLimit === 1 ? '' : 's'} connected.
            </strong>{' '}
            Everyone gets a personal account on every plan, and it does not count against your firm
            allowance - a shop owner has both, so the two are counted separately. Each account needs
            its own API key and its own webhook.
          </Notice>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <Notice>
          <strong>Adding a webhook is optional.</strong> Claiming your shops above already gives you
          benchmarking, competitor comparison and live stock. A webhook adds <em>when</em> sales
          happened and what they earned - and it needs an endpoint reachable over HTTPS from the
          internet, so it will not work against a local install.
        </Notice>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Notice>
          <strong>Use a firm account for webhooks.</strong> A personal feed carries your entire
          financial life - private transfers, purchases, tax, UBI, salary. A firm account is
          business activity only: cleaner data and far less exposure. A solo one-person firm is
          perfectly fine.
        </Notice>
      </div>

      {created && <CreatedPanel result={created} onDismiss={() => setCreated(null)} />}

      <Card title="Your connections">
        {list.length === 0 ? (
          <Empty title="No accounts connected yet">
            <span className="muted">Add one below to start ingesting your sales.</span>
          </Empty>
        ) : (
          <div className="stack">
            {list.map((connection) => (
              <ConnectionRow
                key={connection.id}
                connection={connection}
                onTest={setTestPayload}
                onChanged={connections.reload}
                onShowSetup={async () => setCreated(await api.onboarding.instructions(connection.id))}
              />
            ))}
          </div>
        )}
      </Card>

      {testPayload && (
        <Card
          title="Signed test delivery"
          subtitle="A real signature over a sample sale - replaying it exercises the same verification path a live event takes"
          actions={
            <button className="ghost" onClick={() => setTestPayload(null)}>
              Close
            </button>
          }
        >
          <pre style={{ whiteSpace: 'pre-wrap' }}>{testPayload.curl}</pre>
        </Card>
      )}

      <ConnectForm
        form={form}
        setForm={setForm}
        error={error}
        busy={busy}
        onSubmit={submit}
      />
    </>
  );
}

function CreatedPanel({
  result,
  onDismiss,
}: {
  result: CreateConnectionResult;
  onDismiss: () => void;
}) {
  return (
    <Card
      title="Connect the webhook"
      subtitle="Done on the DemocracyCraft site - no API key at any point"
      actions={
        <button className="ghost" onClick={onDismiss}>
          Dismiss
        </button>
      }
    >
      <div className="stack">
        <div className="key-destination">
          <p className="small">{result.instructions.summary}</p>
          <p className="small muted">
            <a href={result.instructions.guideUrl} target="_blank" rel="noreferrer">
              Official webhook guide
            </a>
          </p>
        </div>

        {/* The values the DemocracyCraft form asks for, laid out as the fields
            they are. Previously this was one endpoint in a <pre> and the rest
            buried in prose, which meant people guessed at the other boxes. */}
        <div className="webhook-fields">
          {result.instructions.fields.map((field) => (
            <div className="webhook-field" key={field.name}>
              <div className="webhook-field-name">{field.name}</div>
              <div className="webhook-field-value">
                {field.value == null ? (
                  <span className="muted">Given to you by DemocracyCraft</span>
                ) : field.copyable ? (
                  <CopyValue value={field.value} />
                ) : (
                  <code>{field.value}</code>
                )}
              </div>
              <div className="small muted">{field.hint}</div>
            </div>
          ))}
        </div>

        <ol className="setup-steps">
          {result.instructions.steps.map((step) => (
            <li key={step.title}>
              <strong>{step.title}</strong>
              <div className="small muted">{step.detail}</div>
              {step.link && (
                <div className="small" style={{ marginTop: 4 }}>
                  <a href={step.link} target="_blank" rel="noreferrer">
                    {step.link}
                  </a>
                </div>
              )}
              {step.command && <pre style={{ whiteSpace: 'pre-wrap' }}>{step.command}</pre>}
            </li>
          ))}
        </ol>

        <div>
          <div className="small muted">
            Paste the <strong>signing secret</strong> shown after you click Create
          </div>
          <SecretForm connectionId={result.connection.id} />
        </div>

        <div>
          <div className="small muted" style={{ marginBottom: 6 }}>
            Managing it later
          </div>
          <ul className="muted small" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
            {result.instructions.manageWebhook.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        <Notice tone="warn">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {result.instructions.requirements.map((line) => (
              <li key={line} className="small">
                {line}
              </li>
            ))}
          </ul>
        </Notice>
      </div>
    </Card>
  );
}

function ConnectionRow({
  connection,
  onTest,
  onChanged,
  onShowSetup,
}: {
  connection: {
    id: string;
    label: string | null;
    dcAccountCode: string;
    accountType: string;
    eventCount: number;
    lastEventAt: string | null;
    shopEventsOnly: boolean;
    webhookStatus: keyof typeof STATUS_TONE;
    ingestUrl: string;
    shops: Array<{ id: string; name: string }>;
  };
  onTest: (payload: TestPayload) => void;
  onChanged: () => void;
  onShowSetup: () => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: '12px 14px',
      }}
    >
      <div className="spread" style={{ marginBottom: 8, alignItems: 'flex-start' }}>
        <div>
          <strong>{connection.label ?? connection.dcAccountCode}</strong>{' '}
          <span className="small muted">({connection.accountType})</span>
          <div className="small muted">
            {number(connection.eventCount)} events - last {relativeTime(connection.lastEventAt)}
            {connection.shopEventsOnly ? ' - filtered to shop sales' : ''}
          </div>
        </div>
        <div className="row">
          <Badge tone={STATUS_TONE[connection.webhookStatus]}>{connection.webhookStatus}</Badge>
          <button className="ghost" onClick={onShowSetup}>
            Setup instructions
          </button>
          <button
            className="ghost"
            onClick={async () => onTest(await api.onboarding.testPayload(connection.id))}
          >
            Test payload
          </button>
          <button
            className="ghost"
            onClick={async () => {
              const result = await api.onboarding.rotateSecret(connection.id);
              window.alert(`New signing secret - update it in game:\n\n${result.webhookSecret}`);
              onChanged();
            }}
          >
            Rotate secret
          </button>
        </div>
      </div>
      <pre>{connection.ingestUrl}</pre>
      {connection.shops.length > 0 && (
        <div className="small muted" style={{ marginTop: 8 }}>
          Shops: {connection.shops.map((shop) => shop.name).join(', ')}
        </div>
      )}
    </div>
  );
}

interface ConnectFormState {
  accountType: 'firm' | 'personal';
  dcAccountCode: string;
  privacyAcknowledged: boolean;
}

function ConnectForm({
  form,
  setForm,
  error,
  busy,
  onSubmit,
}: {
  form: ConnectFormState;
  setForm: (next: ConnectFormState) => void;
  error: string | null;
  busy: boolean;
  onSubmit: () => void;
}) {
  return (
    <Card title="Connect another account">
      <div className="stack" style={{ maxWidth: 520 }}>
        <div className="field">
          <label htmlFor="type">Account type</label>
          <select
            id="type"
            value={form.accountType}
            onChange={(e) =>
              setForm({ ...form, accountType: e.target.value as 'firm' | 'personal' })
            }
          >
            <option value="firm">Firm (recommended)</option>
            <option value="personal">Personal</option>
          </select>
        </div>

        <div className="field">
          <label htmlFor="code">{form.accountType === 'firm' ? 'Firm name' : 'Account name'}</label>
          <input
            id="code"
            value={form.dcAccountCode}
            placeholder={form.accountType === 'firm' ? 'Redstone and Co' : 'YourPlayerName'}
            onChange={(e) => setForm({ ...form, dcAccountCode: e.target.value })}
          />
        </div>

        {form.accountType === 'personal' && (
          <Notice tone="warn">
            <label className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
              <input
                type="checkbox"
                style={{ width: 'auto', marginTop: 3 }}
                checked={form.privacyAcknowledged}
                onChange={(e) => setForm({ ...form, privacyAcknowledged: e.target.checked })}
              />
              <span className="small">
                I understand a personal feed includes non-shop money movement. Events are filtered to
                shop sales before storage, and no spending token is ever requested - but a firm
                account remains the recommended option.
              </span>
            </label>
          </Notice>
        )}

        {error && <div className="error-text">{error}</div>}

        <div>
          <button
            className="primary"
            onClick={onSubmit}
            disabled={
              busy ||
              !form.dcAccountCode.trim() ||
              (form.accountType === 'personal' && !form.privacyAcknowledged)
            }
          >
            {busy ? 'Creating...' : 'Create connection'}
          </button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Claim your shops from public market data.
 *
 * This is the fastest path to something useful: no token, no webhook, no
 * publicly reachable endpoint. If the market index has crawled the items you
 * sell, your own sign prices and stock levels are already there.
 */
function ClaimShops({ onClaimed }: { onClaimed: () => void }) {
  const [name, setName] = useState('');
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.onboarding.lookup>> | null>(
    null,
  );
  const [claimed, setClaimed] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<
    (Awaited<ReturnType<typeof api.onboarding.verifyStart>> & { hint?: string | null }) | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = async () => {
    setBusy(true);
    setError(null);
    setClaimed(null);
    try {
      setResult(await api.onboarding.lookup(name.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const claim = async () => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await api.onboarding.claim({ ownerName: name.trim() });
      setClaimed(
        `Claimed ${outcome.shopsFound} listings across ${outcome.itemsListed} items in ${outcome.worlds.join(', ') || 'your world'}.`,
      );
      setResult(null);
      setChallenge(null);
      onClaimed();
    } catch (err) {
      // A 402 here is the ownership challenge, not a billing problem: the
      // preview is open because the data is public, but claiming needs proof.
      if (err instanceof UpgradeRequiredError || (err as ApiError)?.status === 402) {
        try {
          setChallenge(await api.onboarding.verifyStart(name.trim()));
          setError(null);
        } catch {
          setError('Could not start verification');
        }
      } else {
        setError(err instanceof Error ? err.message : 'Could not claim those shops');
      }
    } finally {
      setBusy(false);
    }
  };

  const recheck = async () => {
    setBusy(true);
    try {
      const status = await api.onboarding.verifyStatus(name.trim());
      if (status.status === 'verified') {
        await claim();
      } else {
        setChallenge((current) =>
          current ? { ...current, hint: status.hint ?? null } : current,
        );
        if (status.hint) setError(status.hint);
        else setError('Payment not seen yet. It can take a few seconds.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Find your shops"
      subtitle="Your in-game name, as it appears on your ChestShop signs. No token needed."
    >
      <div className="row" style={{ alignItems: 'flex-end', marginBottom: 12 }}>
        <div className="field" style={{ marginBottom: 0, minWidth: 240 }}>
          <label htmlFor="ownerName">In-game name</label>
          <input
            id="ownerName"
            value={name}
            placeholder="YourPlayerName"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) void lookup();
            }}
          />
        </div>
        <button onClick={lookup} disabled={busy || !name.trim()}>
          {busy ? 'Searching...' : 'Search'}
        </button>
      </div>

      {error && <div className="error-text">{error}</div>}
      {claimed && <Notice>{claimed} Your dashboard is live.</Notice>}

      {challenge && (
        <Notice tone="warn">
          <strong>Verify you own {challenge.ownerName}</strong>
          <p className="small" style={{ marginTop: 6 }}>
            Anyone can look up a shop - the data is public - but claiming one attaches it to your
            dashboard and alerts, so we need proof you control it.
          </p>
          <p className="small">
            In game, run this <em>from the account you are claiming</em>:
          </p>
          <pre>{challenge.command}</pre>
          <p className="small muted">
            The amount is unique to you, so we match it automatically. The payer name is what proves
            ownership - paying from a different account will not verify.
          </p>
          <button className="primary" onClick={recheck} disabled={busy}>
            {busy ? 'Checking...' : 'I have paid - check now'}
          </button>
        </Notice>
      )}

      {result && (
        <>
          <div className="spread" style={{ marginBottom: 10 }}>
            <span className="muted small">{result.note}</span>
            {result.found > 0 && (
              <button className="primary" onClick={claim} disabled={busy}>
                Claim these {result.found} listings
              </button>
            )}
          </div>
          {result.found > 0 && (
            <DataTable
              rows={result.listings}
              rowKey={(listing) => String(listing.shopId)}
              initialSort={{ key: 'item', dir: 'asc' }}
              searchPlaceholder="Search items"
              pageSize={12}
              columns={[
                {
                  key: 'item',
                  header: 'Item',
                  searchText: (listing) => `${listing.itemName} ${listing.material}`,
                  sortValue: (listing) => listing.itemName,
                  render: (listing) => (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <ItemIcon material={listing.material} size={20} />
                      {listing.itemName}
                    </span>
                  ),
                },
                {
                  key: 'price',
                  header: 'Unit price',
                  align: 'num',
                  sortValue: (listing) => listing.unitPrice,
                  render: (listing) => unitMoney(listing.unitPrice),
                },
                {
                  key: 'batch',
                  header: 'Batch',
                  align: 'num',
                  sortValue: (listing) => listing.batchQty,
                  render: (listing) => number(listing.batchQty),
                },
                {
                  key: 'stock',
                  header: 'Stock',
                  align: 'num',
                  sortValue: (listing) => listing.currentStock,
                  render: (listing) =>
                    listing.currentStock === 0 ? (
                      <Badge tone="warning">empty</Badge>
                    ) : (
                      number(listing.currentStock ?? 0)
                    ),
                },
                {
                  key: 'world',
                  header: 'Location',
                  searchText: (listing) => listing.world ?? '',
                  sortValue: (listing) => listing.world,
                  render: (listing) => <span className="muted small">{listing.world ?? '-'}</span>,
                },
              ]}
            />
          )}
        </>
      )}
    </Card>
  );
}

/** Accepts the signing secret the DC API generated when the owner registered. */
/**
 * A value with a copy button.
 *
 * The payload URL has to match character for character on the other side, and
 * people were selecting it out of a <pre> by hand. A trailing space costs an
 * afternoon of "why is nothing arriving".
 */
function CopyValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <span className="copy-value">
      <code>{value}</code>
      <button
        className="ghost"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            // Clipboard access can be refused (insecure origin, permissions).
            // The value is on screen and selectable, so this is not fatal.
            setCopied(false);
          }
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

function SecretForm({ connectionId }: { connectionId: string }) {
  const [secret, setSecret] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <div className="row" style={{ alignItems: 'flex-end' }}>
      <div className="field" style={{ marginBottom: 0, minWidth: 300 }}>
        <label htmlFor="secret">Signing secret from the API response</label>
        <input id="secret" value={secret} onChange={(e) => setSecret(e.target.value)} />
      </div>
      <button
        disabled={busy || secret.trim().length < 8}
        onClick={async () => {
          setBusy(true);
          try {
            await api.onboarding.setSecret(connectionId, secret.trim());
            setSaved(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Saving...' : 'Save secret'}
      </button>
      {saved && <span className="delta-up small">Saved</span>}
    </div>
  );
}
